import { ADDON_STATUS, PACKAGE_TYPE, THIRD_PARTY_PARTNER_NAMES } from '@intigral/constant';
import { IAddonType, IServiceCollectionDetails, IUserFullObject, UserSubscriptionDetails } from '@intigral/types';
import { i18n } from '../i18n';

/**
 *
 * get default service collection details from plan config
 * @param serviceCollectionDetails serviceCollectionDetails array
 * @returns default service collection details
 */
export const getDefaultServiceCollectionDetails = (
    serviceCollectionDetails: IServiceCollectionDetails[]
): IServiceCollectionDetails | null => {
    return serviceCollectionDetails?.find((serviceCollection) => serviceCollection?.default) || null;
};

/**
 *
 * filter addons by service collection
 * @param AddonList addons array
 * @param serviceCollectionId serviceCollectionId
 * @returns filtered free addons with service collection and paid is as it is
 */
export const filterFreeAndPaidAddonsByServiceCollection = (
    AddonList: IAddonType[],
    serviceCollectionId: string
): IAddonType[] => {
    return AddonList?.filter((addon) => {
        if (addon?.serviceCollectionId) {
            return addon.serviceCollectionId?.toLowerCase() === serviceCollectionId?.toLowerCase();
        }
        return true;
    });
};

/**
 *
 * get free or paid addon from list of addons
 * @param  addons addons array
 * @param type 'free' or 'paid'
 * @returns default service collection details
 */

export const getFreeOrPaidAddons = (addons: IAddonType[], type: 'free' | 'paid', serviceCollectionId?: string) => {
    const isFree = type === 'free';
    return addons?.filter((addon) => {
        if (addon.packageType === PACKAGE_TYPE.ALACARTE_FREE && addon?.isBaseSubscription) {
            return false;
        }
        return isFree
            ? addon?.serviceCollectionId &&
                  addon.serviceCollectionId?.toUpperCase() == serviceCollectionId?.toUpperCase()
            : !addon?.serviceCollectionId;
    });
};

/**
 *
 * check addon is active and subscribed
 * @param  addons addons array
 * @param user  user object
 * @param pickAndChooseEnabled if pick and choose feature is enabled
 * @param ignoreFreeOrPaidCheck If true, ignores whether the addon is  subscribed as free or paid
 *                              and checks only if the addon is subscribed and active
 * @returns true if addon is active and subscribed
 */

export const isActiveSubscribedAddon = (
    addon: IAddonType,
    user: IUserFullObject,
    pickAndChooseEnabled = false,
    ignoreFreeOrPaidCheck = false
) => {
    const subscribedAddon = getActiveSubscribedAddon(addon, user, pickAndChooseEnabled, ignoreFreeOrPaidCheck);
    return subscribedAddon?.statusName?.toUpperCase() === ADDON_STATUS.STATUS_ACTIVE ? true : false;
};

/**
 *
 * get active and subscribed
 * @param  addons addons array
 * @param user  user object
 * @param pickAndChooseEnabled if pick and choose feature is enabled
 * @param ignoreFreeOrPaidCheck If true, ignores whether the addon is  subscribed as free or paid
 *                              and checks only if the addon is subscribed and active
 * @returns addon if addon is active and subscribed
 *
 */

export const getActiveSubscribedAddon = (
    addon: IAddonType,
    user: IUserFullObject,
    pickAndChooseEnabled = false,
    ignoreFreeOrPaidCheck = false
) => {
    const subscribedAddon = user.subscriptionStatus?.find((_addon) => {
        return checkSubscribedAddon(addon, _addon, pickAndChooseEnabled, ignoreFreeOrPaidCheck);
    });
    return subscribedAddon;
};

/**
 *
 * check addon is active and subscribed
 * @param  addons addons array
 * @param subscriptionStatusAddon  already subscribed addon from subscription status
 * @param pickAndChooseEnabled if pick and choose feature is enabled
 * @param ignoreFreeOrPaidCheck If true, ignores whether the addon is  subscribed as free or paid
 *                              and checks only if the addon is subscribed and active
 * @returns addon if addon is active and subscribed
 *
 */

export const checkSubscribedAddon = (
    addon: IAddonType,
    subscriptionStatusAddon: UserSubscriptionDetails,
    pickAndChooseEnabled = false,
    ignoreFreeOrPaidCheck = false
) => {
    if (
        ![PACKAGE_TYPE.ALACARTE_FREE, PACKAGE_TYPE.ALACARTE].includes(
            (subscriptionStatusAddon?.subPackageType || '').toUpperCase()
        ) &&
        subscriptionStatusAddon.subscriptionGuid?.toUpperCase() === addon.subscriptionGUID?.toUpperCase()
    ) {
        if (pickAndChooseEnabled && !ignoreFreeOrPaidCheck) {
            return addon?.isSubscribed ? true : false;
        }
        return true;
    }
    return false;
};

/**
 *
 * get free active and subscribed addon
 * @param  addons addons array
 * @param user  user object
 * @returns free active and subscribed addon
 */

export const isFreeActiveSubscribedAddon = (addon: IAddonType, user: IUserFullObject, pickAndChooseEnabled = false) => {
    const subscribedAddon = user.subscriptionStatus.find(
        (_addon) => addon?.serviceCollectionId && checkSubscribedAddon(addon, _addon, pickAndChooseEnabled)
    );
    return subscribedAddon?.statusName?.toUpperCase() === ADDON_STATUS.STATUS_ACTIVE ? true : false;
};

/**
 *
 * get paid active and subscribed addon
 * @param  addons addons array
 * @param user  user object
 * @returns paid active and subscribed addon
 */

export const isPaidActiveSubscribedAddon = (
    addon: IAddonType,
    user: IUserFullObject,
    isPickAndChooseEnabled = false
) => {
    const subscribedAddon = user.subscriptionStatus.find(
        (_addon) => !addon?.serviceCollectionId && checkSubscribedAddon(addon, _addon, isPickAndChooseEnabled)
    );
    return subscribedAddon?.statusName?.toUpperCase() === ADDON_STATUS.STATUS_ACTIVE ? true : false;
};

/**
 *
 * check is already subscribed paid addon is now free
 * @param  paidAddon paid addon object
 * @param user user object
 * @param availableAddons available addons already grouped by default service collection id
 * @returns returns true if already subscribed paid addon is now free else false
 */
export const isSubscribedPaidAddonNowFree = (
    paidAddon: IAddonType,
    user: IUserFullObject,
    availableAddons: IAddonType[],
    pickAndChooseEnabled = false
): boolean => {
    if (!paidAddon.serviceCollectionId && isActiveSubscribedAddon(paidAddon, user, pickAndChooseEnabled)) {
        const availableAsFreeAddon = availableAddons.find(
            (addon) => addon.subscriptionGUID === paidAddon.subscriptionGUID && addon.serviceCollectionId
        );
        return availableAsFreeAddon && !isFreeActiveSubscribedAddon(availableAsFreeAddon, user, pickAndChooseEnabled)
            ? true
            : false;
    }
    return false;
};

/**
 *
 * check is free addon is already subscribed as paid addon
 * @param  freeAddon free addon object
 * @param user user object
 * @param availableAddons all addons free and paid / or all already subscribed addon
 * @returns returns true if free addon is already subscribed as paid addon else false
 */
export const isFreeAddonAlreadySubscribedAsPaid = (
    freeAddon: IAddonType,
    user: IUserFullObject,
    availableAddons: IAddonType[],
    pickAndChooseEnabled = false
): boolean => {
    if (freeAddon.serviceCollectionId) {
        const getSubscribedPaidAddon = availableAddons.find(
            (addon) =>
                addon.subscriptionGUID?.toLowerCase() === freeAddon.subscriptionGUID?.toLowerCase() &&
                !addon?.serviceCollectionId
        );
        if (getSubscribedPaidAddon && isPaidActiveSubscribedAddon(getSubscribedPaidAddon, user, pickAndChooseEnabled))
            return true;
    }
    return false;
};
/**
 *
 * get addon banner for addon Detail section
 * @param  bannerType banner type for which banner is required
 * @param maxfreeAddon max free addons allowed
 * @returns returns banner message
 */
export const getAddonDetailBannerText = (
    bannerType:
        | 'complimentaryInfo'
        | 'paidNowFree'
        | 'manageAddoncomplimentaryInfo'
        | 'manageAddonReachQuota'
        | 'AddonDetailReachQuota',
    complimentaryFreeAddon?: string,
    swapFee?: string | number,
    currency?: string
) => {
    switch (bannerType) {
        case 'complimentaryInfo':
            return i18n
                .t('user_profile.account.addon_detail_pick_choose_complimentary_addons_info')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon || '');
        case 'paidNowFree':
            return i18n.t('user_profile.account.addon_detail_pick_choose_paid_addon_now_free');
        case 'manageAddoncomplimentaryInfo':
            return i18n
                .t('user_profile.account.addon_detail_pick_choose_complimentary_addons_info')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon || '');
        case 'manageAddonReachQuota':
            return i18n
                .t('user_profile.account.manage_addon_pick_choose_swap_massage')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon || '')
                .replace('{swapfee}', (swapFee || '')?.toString())
                .replace('{currency}', currency || '');

        case 'AddonDetailReachQuota':
            return i18n
                .t('user_profile.account.addon_detail_pick_choose_swap_massage')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon || '')
                .replace('{swapfee}', (swapFee || '')?.toString())
                .replace('{currency}', currency || '');
        default:
            return '';
    }
};

/**
 *
 * get addon banner for addon list section
 * @param  bannerType banner type for which banner is required
 * @param maxfreeAddon max free addons allowed
 * @returns returns banner message
 */

type BannerType = 'complimentaryAddon' | 'quotaReached' | 'paidInfo' | 'swapInfo';

export const getAddonListBannerText = (
    bannerType: BannerType,
    complimentaryFreeAddon?: number,
    swapFee?: string | number,
    currency?: string,
    planName?: string
) => {
    switch (bannerType) {
        case 'complimentaryAddon':
            return i18n
                .t('user_profile.account.addon_list_pick_choose_complimentary_addons_info')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon?.toString() || '')
                .replace('{planName}', planName || '');
        case 'quotaReached':
            return i18n
                .t('user_profile.account.addon_list_pick_choose_free_quota_reached')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon?.toString() || '');
        case 'paidInfo':
            return i18n.t('user_profile.account.addon_list_pick_choose_paid_addon_info');
        case 'swapInfo':
            return i18n
                .t('user_profile.account.addon_list_pick_choose_swap_free_addon_info')
                .replace('{complimentaryAddonsCount}', complimentaryFreeAddon?.toString() || '')
                .replace('{swapfee}', (swapFee || '')?.toString())
                .replace('{currency}', currency || '');
        default:
            return '';
    }
};

/**
 *
 * sort addons based on subscribed addons
 * @param  addons list of addons
 * @param user user object to get the subscribed addons
 * @returns sorted list of addons
 */
export const sortAddonBasedOnSubscribedAddons = (
    addons: IAddonType[],
    user: IUserFullObject,
    pickAndChooseEnabled = false
) => {
    return addons.sort(
        (a, b) =>
            Number(isActiveSubscribedAddon(b, user, pickAndChooseEnabled)) -
            Number(isActiveSubscribedAddon(a, user, pickAndChooseEnabled))
    );
};

/**
 *
 *  check if partner name exist in addons list like nextflix addon is exist or not in addon list
 * @param  addons list of addons
 * @param partnerName partner name to check
 * @returns true if partner name exist else false
 */
export const isPartnerNameExist = (addons: IAddonType[], partnerName: string) => {
    return addons.find((addon) => addon?.partnerName?.toLowerCase() === partnerName?.toLowerCase());
};

/**
 *
 * check addon is hard bundle or not
 * @param  addon  addon detail object
 * @returns return true if addon is hard bundle else false
 */

export const isHardBundleAddon = (addons: IAddonType) => {
    // return addons?.isUnsubscribeAllowed === false;
    return addons?.isDefaultSubscription === true;
};

/**
 * Deduplicates addons by subscriptionGuid.
 *
 * Rules:
 * - If two addons share the same subscriptionGuid, keep the one WITHOUT serviceCollectionId.
 * - If only one addon exists for that subscriptionGuid, keep it as-is.
 */
export function getUniqueAddons(addons: IAddonType[]) {
    const map = addons.reduce<Record<string, IAddonType>>((acc, addon) => {
        const key = addon.subscriptionGUID;
        // If key not yet added → store addon
        if (!acc[key]) {
            acc[key] = addon;
        } else {
            const existing = acc[key];

            // Prefer addon WITHOUT serviceCollectionId
            if (!addon.serviceCollectionId && existing.serviceCollectionId) {
                acc[key] = addon;
            }
            // Otherwise keep existing
        }

        return acc;
    }, {});

    return Object.values(map);
}
/**
 * check if it is netflix addon or not
 * @param partnerName addon partner name
 * @returns retun true if netflix addon
 */
export const isNetflixAddon = (partnerName = '') => {
    return partnerName?.toUpperCase() === THIRD_PARTY_PARTNER_NAMES.NETFLIX;
};

/**
 * get already subscribed netflix addons
 * @param alreadySubscribedAddons  already subscribed addons
 * @param user  user object
 * @param isPickAndChooseEnabled flag to check pick and choose enabled
 * @returns  return addon if it is already subcribed and netflix addon
 */
export const getAlreadySubNetflixPaidAddon = (
    alreadySubscribedAddons: IAddonType[],
    user: IUserFullObject,
    isPickAndChooseEnabled: boolean
) => {
    return alreadySubscribedAddons.find((addon) => {
        if (isNetflixAddon(addon?.partnerName) && isPaidActiveSubscribedAddon(addon, user, isPickAndChooseEnabled)) {
            return true;
        }
        return false;
    });
};

/**
 * to check available netflix addon is consumed
 * @param freeAddon  free netflix addon
 * @param alreadySubscribedAddons  already subscribed addons
 * @param user user object
 * @param isPickAndChooseEnabled  to check pick and choose is enable
 * @returns paid addon if available free netflix addon is consumed
 */

export const isFreeNetflixConsumed = (
    freeAddon: IAddonType,
    alreadySubscribedAddons: IAddonType[],
    user: IUserFullObject,
    maxFreeAddons: number,
    isPickAndChooseEnabled: boolean
) => {
    /* if paid(premium) is Subscribed and free lower addon(standard) is available as free with service collection id and isSubscribed true then
     * paid addon will be for differential price
     * free quota will be consumend and standard will be treated as subscribed or consumed
     */
    if (
        freeAddon?.serviceCollectionId &&
        isNetflixAddon(freeAddon?.partnerName) &&
        freeAddon?.isSubscribed &&
        !isFreeActiveSubscribedAddon(freeAddon, user, isPickAndChooseEnabled) // this checks in status api also but in this case it will not come in status api
    ) {
        // if basic is available as free then standard and premium will be paid with differential price.
        // if standard is available as free then basic will not be there at all and premium will be with differential price.
        const paidNetflixAddon = getAlreadySubNetflixPaidAddon(alreadySubscribedAddons, user, isPickAndChooseEnabled);
        if (paidNetflixAddon && Number(freeAddon?.subRank) < Number(paidNetflixAddon?.subRank)) {
            /*
             * if quota is already consumed then it consider as not consumed
             * here we are not including free netflix to check the quota
             */
            const alreadySubFreeAddon = alreadySubscribedAddons.filter((addon) => {
                return (
                    isFreeActiveSubscribedAddon(addon, user, isPickAndChooseEnabled) &&
                    !addon?.isFreeNetflixConsumedDirectly
                );
            })?.length;
            if (Number(maxFreeAddons) > alreadySubFreeAddon) {
                return paidNetflixAddon;
            }
        }
    }
    return null;
};

/**
 * to check is Netflix paid addon purchased with delta price and avialble free addon quota is consumed
 * @param paidAddon  paid addon
 * @param availableAddons available free addon
 * @param user user object
 * @param isPickAndChooseEnabled to check pick and choose is enable
 * @returns free paid netflix addon purchased with delta price and consumed quota
 */
export const isNetflixPaidConsumedFreeQuota = (
    paidAddon: IAddonType,
    availableAddons: IAddonType[],
    user: IUserFullObject,
    isPickAndChooseEnabled: boolean,
    alreadySubscribedAddons: IAddonType[],
    maxFreeAddons: number
) => {
    /* if paid(premium) is Subscribed and free lower addon(standard) is available as free with service collection id  and isSubscribed true then
     * paid addon will be for differential price
     * free quota will be consumend and standard will be treated as subscribed or consumed
     */
    if (
        isNetflixAddon(paidAddon?.partnerName) &&
        isPaidActiveSubscribedAddon(paidAddon, user, isPickAndChooseEnabled)
    ) {
        // if basic is available as free with isSubscribed then standard and premium will be paid with differential price.
        // if standard is available as free then basic will not be there at all and premium will be with differential price.
        return availableAddons?.find((avlAddon) => {
            if (isFreeNetflixConsumed(avlAddon, alreadySubscribedAddons, user, maxFreeAddons, isPickAndChooseEnabled)) {
                return true;
            }
            return false;
        });
    }
    return false;
};
